import { Link } from "react-router-dom";
import { AsciiField, AsciiBanner, Button, Label, Terminal, AsciiMeter, Pipeline, FeatureCard, Stat, Nav } from "../components/ascii";
import SEO, { SEO_CONFIG } from "../components/SEO";
import graspdLogo from "../assets/graspd-logo.svg";
import "./Home.css";

// Copy is drawn from the current graspd.ai enterprise page. Rules: no em dashes,
// no "it's not X, it's Y", mono UI text lowercase, never invent a metric.

const NAV_LINKS = [
  { label: "architecture", href: "#architecture" },
  { label: "audit", href: "#audit" },
  { label: "security", href: "#security" },
  { label: "enterprise", href: "/", site: true, current: true },
  { label: "academy", href: "/academy", site: true },
];

const MAILTO = "mailto:contact@graspd.ai?subject=Graspd%20Enterprise%20Inquiry";
const PILOT_HREF = MAILTO;

const TICKER = "ingest ── parse claims ── author ── expert review ── freeze spec ── personalize ── train ── prove ── ".repeat(8);

const SPEC_ART = "┌───────────────────────────────────────┐\n│ claim 0412  <-  sop-114  r7  l.42     │\n│ claim 0413  <-  sop-114  r7  l.48     │\n│ claim 0414  <-  wi-022   r3  l.07     │\n└───────────────────┬───────────────────┘\n       ┌────────────┼────────────┐\n       ▼            ▼            ▼\n [ new hire ] [ operator ] [ engineer ]\n + the basics   skips 60%    deep dive\n\nRENDER  a personal path, the same claims";

const STEPS = [
  { tag: "ingest", name: "Ingest", detail: "Drop in SOPs, work instructions and specs. Graspd parses them into atomic, cited claims.", out: "claims.jsonl" },
  { tag: "author", name: "Author & review", detail: "Lessons are drafted from those claims, then pass approval gates run by your own experts.", out: "spec.r7 (frozen)" },
  { tag: "prove", name: "Train & prove", detail: "Each learner gets a personalized path. Each completion is logged against the revision it taught.", out: "audit.log" },
];

const BUILD_LOG = [
  { t: "cmd", text: "graspd ingest ./sops --rev r7" },
  { t: "out", text: "  parsed 114 documents into cited claims" },
  { t: "cmd", text: "graspd build --spec sop-114.r7" },
  { t: "out", text: "  review gate  tier 2  sampled + approved" },
  { t: "hl", text: "  spec frozen  sha256:9f3a..c21e  prev:41bd..07aa" },
  { t: "cmd", text: "graspd verify --rebuild sop-114.r7" },
  { t: "ok", text: "  ok  byte-identical rebuild, chain intact" },
];

// Illustrative only. Keep the "example cohort" label if this stays on the page.
const COHORT = [
  { label: "lockout / tagout", value: 92 },
  { label: "pfmea fundamentals", value: 71, target: 90 },
  { label: "furnace sop r7", value: 38, target: 90 },
  { label: "calibration wi-022", value: 55, target: 90 },
  { label: "esd handling", value: 84, target: 90 },
];

const AUDIENCES = [
  ["Deep-tech manufacturing", "novel materials, battery chemistry, aerospace"],
  ["Life sciences", "validated processes, controlled documents"],
  ["Financial services", "policy attestation, regulator-ready records"],
  ["Energy", "field procedures, safety-critical work"],
];

const FOOTER_LINKS = [
  { label: "enterprise", href: "/" },
  { label: "architecture", href: "#architecture" },
  { label: "audit", href: "#audit" },
  { label: "security", href: "#security" },
  { label: "academy", to: "/academy" },
  { label: "privacy", to: "/privacy" },
  { label: "terms", to: "/terms" },
  { label: "contact", href: MAILTO },
];

export default function Home() {
  return (
    <div className="hp gd-root">
      <SEO {...SEO_CONFIG.home} />
      <Nav links={NAV_LINKS} cta="request a pilot" ctaHref={PILOT_HREF} logo={graspdLogo} />

      {/* Hero */}
      <section className="hp-hero-sec">
        <div className="wrap hp-hero">
          <div>
            <h1 className="hp-h1">Your documents.<br /><span>Provably</span> learned.</h1>
            <p className="hp-lede">Graspd turns SOPs and work instructions into personalized training. Every lesson traces to its source line, and every record holds up in an audit.</p>
            <div className="hp-ctas">
              <Button size="lg" arrow href={PILOT_HREF}>request a pilot</Button>
              <Button size="lg" variant="secondary" brackets href="#architecture">see a build log</Button>
            </div>
            <p className="hp-fine">made for teams audited under IATF 16949 · ISO 9001 · OSHA</p>
          </div>
          <div className="hp-fig">
            <AsciiField cols={66} rows={36} />
            <div className="hp-cap"><span>fig.01  your procedures, as a graph</span><span>rot 0.35 rad/s</span></div>
          </div>
        </div>
      </section>

      <div className="hp-ticker" aria-hidden="true"><div>{TICKER + TICKER}</div></div>

      {/* 01 skill debt */}
      <section>
        <div className="wrap">
          <div className="hp-split">
            <div>
              <Label>01 skill debt</Label>
              <h2 className="hp-h2">Skill debt compounds quietly.</h2>
              <p className="hp-body">Your senior engineers carry the real procedures in their heads. When they leave, the training leaves with them. Auditors ask which revision a person was trained on, and the answer lives in a spreadsheet nobody trusts.</p>
            </div>
            <div className="hp-panel">
              <div className="hp-panel-h"><Label variant="bracket">example cohort / line 3</Label><Label variant="badge">target 90%</Label></div>
              {COHORT.map((m) => <AsciiMeter key={m.label} {...m} />)}
            </div>
          </div>
          <div className="hp-stats">
            <Stat value="6-18" unit=" mo" label="to author a single training program by hand." />
            <Stat value="1:1" label="every training record tied to an exact document revision." />
            <Stat value="0" label="bytes of customer data used to train AI models." />
          </div>
        </div>
      </section>

      {/* 02 how it works */}
      <section id="architecture">
        <div className="wrap">
          <Label>02 how it works</Label>
          <h2 className="hp-h2">From binder to proof in three steps.</h2>
          <Pipeline steps={STEPS} />
          <div className="hp-spec">
            <div>
              <Label>spec / render</Label>
              <h2 className="hp-h2" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-.03em" }}>One frozen spec. A different path for every learner.</h2>
              <p className="hp-body">Personalization changes how a lesson is delivered, never what it claims. So the record means the same thing for a new hire and a twenty-year operator.</p>
              <div className="hp-specart">
                <div className="hp-spechead"><b>SPEC sop-114.r7</b>  frozen · audited · one per program</div>
                <pre>{SPEC_ART}</pre>
              </div>
            </div>
            <Terminal title="build.log" status="live" featured animate lines={BUILD_LOG} />
          </div>
        </div>
      </section>

      {/* 03 audit */}
      <section id="audit">
        <div className="wrap">
          <Label>03 audit</Label>
          <h2 className="hp-h2">Built for the person who signs off.</h2>
          <div className="hp-grid4">
            <FeatureCard featured label="traceability" title="Every claim has a source" glyph={"sop.r7 ─┐\nl.42 ───┼─▶ claim\nl.88 ───┘"}>Click any lesson line and land on the document revision and line it came from.</FeatureCard>
            <FeatureCard label="audit log" title="Hash-chained history" glyph={"[#1]─[#2]─[#3]\n └─sha─┘└─sha─┘"}>An append-only log. Alter one record and the chain breaks where everyone can see it.</FeatureCard>
            <FeatureCard label="builds" title="Reproducible builds" glyph={"build a ═╗\n         ╠═ ==\nbuild b ═╝"}>Rebuild any program from its spec and get the same output, byte for byte.</FeatureCard>
            <FeatureCard label="review" title="Human review gates" glyph={"tier 1 ░░░░ 10%\ntier 2 ▒▒▒▒ 25%\ntier 3 ████ 100%"}>Experts approve content, sampled by risk tier. Critical content is sampled up to 100%.</FeatureCard>
          </div>
        </div>
      </section>

      {/* 04 security: inverted with the terminal theme */}
      <section id="security" className="hp-dark" data-theme="terminal">
        <div className="wrap" style={{ position: "relative" }}>
          <div className="hp-bg" aria-hidden="true"><AsciiField mode="wave" cols={60} rows={26} speed={0.8} /></div>
          <div style={{ position: "relative", maxWidth: 620 }}>
            <Label>04 security</Label>
            <h2 className="hp-h2">Your procedures stay yours.</h2>
            <p className="hp-body">Your documents are the most sensitive thing you own. We built the storage layer like we would want it for ours.</p>
            <div className="hp-checks">
              <div><span className="x">[x] </span>per-tenant storage and encryption keys</div>
              <div><span className="x">[x] </span>row-level security on every query</div>
              <div><span className="x">[x] </span>your data never trains AI models</div>
              <div><span className="o">[ ] </span><span className="o">SOC 2 Type I. not yet. we will tell you when.</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* 05 who it is for */}
      <section>
        <div className="wrap">
          <Label>05 who it is for</Label>
          <h2 className="hp-h2">Where a missed step costs more than a retrain.</h2>
          <div className="hp-who">
            {AUDIENCES.map(([name, detail], i) => (
              <div key={name}><span>0{i + 1}</span><b>{name}</b><span>{detail}</span></div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="pilot" className="hp-cta">
        <div className="wrap">
          <Label live active>selectively onboarding</Label>
          <h2 className="hp-h2">Your first program, live in weeks.</h2>
          <p className="hp-body">Send us one SOP. We will hand back a reviewed, personalized course and the audit trail to prove it.</p>
          <div className="hp-ctas">
            <Button size="lg" arrow href={PILOT_HREF}>request a pilot</Button>
            <Button size="lg" variant="ghost" arrow href="mailto:contact@graspd.ai">talk to a founder</Button>
          </div>
        </div>
      </section>

      <footer className="hp-foot">
        <div className="wrap">
          <AsciiBanner reveal duration={1600} />
          <div className="hp-foot-row">
            <div>
              {FOOTER_LINKS.map((l) =>
                l.to ? (
                  <Link key={l.label} to={l.to}>{l.label}</Link>
                ) : (
                  <a key={l.label} href={l.href}>{l.label}</a>
                )
              )}
            </div>
            <span>© 2026 graspd  ·  pay off skill debt</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
